import axios from 'axios';
import { auth } from '../../firebase-config';

const baseURL = process.env.REACT_APP_BACKEND_URL;


const apiClient = axios.create({
  baseURL: baseURL,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(async function (config) {
  try {
    // First try to get token from localStorage (for fast access)
    let token = localStorage.getItem('authToken');
    const tokenTimestamp = localStorage.getItem('tokenTimestamp');

    // Check if token is expired (older than 50 minutes)
    const isTokenExpired = tokenTimestamp && (Date.now() - parseInt(tokenTimestamp)) > (50 * 60 * 1000);

    // If no stored token, token is expired, or user exists, get fresh token
    const user = auth.currentUser;
    if (user && (!token || isTokenExpired)) {
      console.log('Getting fresh token (no token or expired)');
      token = await user.getIdToken(true); // Force refresh
      localStorage.setItem('authToken', token);
      localStorage.setItem('tokenTimestamp', Date.now().toString());

      // Broadcast token refresh to other tabs
      if (window.BroadcastChannel) {
        const channel = new BroadcastChannel('auth-channel');
        channel.postMessage({
          type: 'TOKEN_REFRESHED',
          token: token,
          timestamp: Date.now()
        });
        channel.close();
      }
    }

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Remove auth header if no token
      delete config.headers['Authorization'];
    }
  } catch (error) {
    console.warn('Could not get Firebase auth token:', error);
    // If error getting token, remove auth header
    delete config.headers['Authorization'];
  }

  return config;
});

// Response interceptor to handle auth errors and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we get a 401 unauthorized error and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = auth.currentUser;
        if (user) {
          console.log('Token expired, refreshing...');
          // Force refresh the token
          const freshToken = await user.getIdToken(true);
          const tokenTimestamp = Date.now();

          localStorage.setItem('authToken', freshToken);
          localStorage.setItem('tokenTimestamp', tokenTimestamp.toString());

          // Broadcast token refresh to other tabs
          if (window.BroadcastChannel) {
            const channel = new BroadcastChannel('auth-channel');
            channel.postMessage({
              type: 'TOKEN_REFRESHED',
              token: freshToken,
              timestamp: tokenTimestamp
            });
            channel.close();
          }

          // Retry the original request with the new token
          originalRequest.headers['Authorization'] = `Bearer ${freshToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
        // Only clear auth-specific keys so Firebase's own storage is preserved
        ['authToken', 'tokenTimestamp', 'userId', 'userName', 'userEmail',
          'userCreatedAt', 'userLastLogin', 'companyId', 'companyName'].forEach(
          key => localStorage.removeItem(key)
        );

        // Do NOT broadcast LOGOUT — a token refresh failure in this tab should
        // not force every other open tab to sign out too.

        // Redirect to login page
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
